"use client";
import { useRef, useState } from "react";
import styles from "./index.module.css";
import {
  postComment,
  updateComment,
} from "../../lib/services/conversation-service";
import { useToken } from "../../lib/context/auth-context";

import ChatArea from "./ChatArea";
import ChatBox from "./ChatBox";
import ChatHeader from "./ChatHeader";

const ChatWindow = ({
  loggedInUser,
  isChatOpen,
  closeChat,
  chatInfo = {},
  chatPayload = {},
  markCommentsAsRead,
  presetMessage,
  clearPresetMessage,
  activateGuestMode,
  chatGuestMode,
  chatBoxFetchError,
  handleBoxRetry,
  showSenderName = false,
}) => {
  const { sectionId, sectionName } = chatInfo;

  const chatAreaRef = useRef(null);
  const [readyForPagination, setReadyForPagination] = useState(false);
  const [savedScrollTop, setSavedScrollTop] = useState(null);
  const [replyTo, setReplyTo] = useState();
  const [editMessage, setEditMessage] = useState(null);
  const scrollToMessageRef = useRef(null);
  const { token } = useToken();
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const [chatState, setChatState] = useState({
    data: [],
    total: 0,
    limit: 15,
    page: 1,
    currentPage: 1,
  });

  // helper callbacks
  const addMessage = (msg) =>
    setChatState((prev) => ({ ...prev, data: [...prev.data, msg] }));

  const replaceMessage = (trackId, realMsg) =>
    setChatState((prev) => ({
      ...prev,
      data: prev.data.map((m) =>
        m.trackId === trackId
          ? {
              ...m,
              ...realMsg,
              trackId: m.trackId,
              sending: false,
              failed: false,
            }
          : m
      ),
    }));

  const markFailed = (trackId) =>
    setChatState((prev) => ({
      ...prev,
      data: prev.data.map((m) =>
        m.trackId === trackId
          ? {
              ...m,
              sending: false,
              failed: true,
              retryCount: (m.retryCount || 0) + 1,
            }
          : m
      ),
    }));

  const updateMessage = (id, updater) =>
    setChatState((prev) => ({
      ...prev,
      data: prev.data.map((m) => (m.trackId === id ? updater(m) : m)),
    }));

  const retryMessage = async (msg) => {
    // cancel any scheduled retry for this message
    if (msg.retryTimeoutId) {
      clearTimeout(msg.retryTimeoutId);
    }
    // clear fail state
    updateMessage(msg.trackId, (m) => ({ ...m, sending: true, failed: false }));

    try {
      let res;
      if (msg.commentEdit) {
        res = await updateComment(msg.trackId, { comment: msg.comment }, token);
        if (res.success) {
          replaceMessage(msg.trackId, {
            ...msg,
            ...res.data.comment,
            trackId: msg.trackId,
            sending: false,
            failed: false,
            retryCount: 0,
            retryTimeoutId: null,
          });
          return;
        }
      } else {
        const payload = {
          ...chatPayload,
          trackId: msg.trackId,
          comment: msg.comment,
          commentType: "MESSAGE",
          expire: false,
          memberId: null,
          reference: msg.reference || null,
          media: msg.media || [],
        };
        res = await postComment(payload, sectionId, sectionName, token);
        if (res.success) {
          replaceMessage(msg.trackId, {
            ...msg,
            ...res.data,
            trackId: msg.trackId,
            sending: false,
            failed: false,
            retryCount: 0,
            retryTimeoutId: null,
          });
          return;
        }
      }

      throw new Error("Send failed");
    } catch (err) {
      console.error("Retry failed:", err);
      // auto retry up to 3 times with exponential backoff
      const retries = (msg.retryCount || 0) + 1;
      if (retries < 3) {
        const delay = Math.min(1000 * Math.pow(2, retries), 10000); // 1s, 2s, 4s, cap 10s
        const timeoutId = setTimeout(
          () =>
            retryMessage({ ...msg, retryCount: retries, retryTimeoutId: null }),
          delay
        );

        updateMessage(msg.trackId, (m) => ({
          ...m,
          retryCount: retries,
          retryTimeoutId: timeoutId,
        }));
      } else {
        markFailed(msg.trackId);
      }
    }
  };

  return (
    <>
      {/* Window */}
      <div className={styles.chatWindow}>
        <ChatHeader
          chatAreaRef={chatAreaRef}
          chatInfo={chatInfo}
          isChatOpen={isChatOpen}
          setSavedScrollTop={setSavedScrollTop}
          setReadyForPagination={setReadyForPagination}
          closeChat={closeChat}
        />

        <ChatArea
          isChatOpen={isChatOpen}
          loggedInUser={loggedInUser}
          readyForPagination={readyForPagination}
          setReadyForPagination={setReadyForPagination}
          chatAreaRef={chatAreaRef}
          chatInfo={chatInfo}
          savedScrollTop={savedScrollTop}
          state={chatState}
          setState={setChatState}
          setReplyTo={setReplyTo}
          chatPayload={chatPayload}
          setEditMessage={setEditMessage}
          handleRetry={retryMessage}
          exposeScrollToMessage={(fn) => (scrollToMessageRef.current = fn)}
          onReplaceMessage={replaceMessage}
          onNewMessage={addMessage}
          markCommentsAsRead={markCommentsAsRead}
          setShowScrollToBottom={setShowScrollToBottom}
          closeChat={closeChat}
          activateGuestMode={activateGuestMode}
          chatGuestMode={chatGuestMode}
          chatBoxFetchError={chatBoxFetchError}
          handleBoxRetry={handleBoxRetry}
          showSenderName={showSenderName}
        />

        <ChatBox
          chatAreaRef={chatAreaRef}
          loggedInUser={loggedInUser}
          chatInfo={chatInfo}
          chatPayload={chatPayload}
          onNewMessage={addMessage}
          onReplaceMessage={replaceMessage}
          onMarkFailed={markFailed}
          onUpdateMessage={updateMessage}
          replyTo={replyTo}
          setReplyTo={setReplyTo}
          editMessage={editMessage}
          setEditMessage={setEditMessage}
          scrollToMessage={(id) => scrollToMessageRef.current?.(id)}
          showScrollToBottom={showScrollToBottom}
          presetMessage={presetMessage}
          clearPresetMessage={clearPresetMessage}
          chatGuestMode={chatGuestMode}
        />
      </div>
    </>
  );
};

export default ChatWindow;
